/**
 * SF-5082 — the per-company Eva helpdesk settings shown under "Automatic drafts and replies":
 * what each priority means for the company, and which priorities, reply types and categories
 * Eva may send on her own. The values match sf-api's company fields (and sf-chat-service's
 * lists, same order); sf-api refuses anything else with a 400, so these lists are for showing
 * the choices, not the real check. Missing on the company = the default (today's behaviour).
 */

export type GuidePriority = 'high' | 'medium' | 'low';
export type PriorityGuide = Partial<Record<GuidePriority, string>>;

/** The ticket's limit for one priority description; sf-api and sf-chat-service use the same. */
export const PRIORITY_GUIDE_MAX_LENGTH = 500;

/** Shown highest first. Placeholder = today's rule (Eva's prompt), used while the box is blank. */
export const PRIORITY_GUIDE_FIELDS: ReadonlyArray<{
  key: GuidePriority;
  label: string;
  todaysRule: string;
}> = [
  {
    key: 'high',
    label: 'What high priority means for this company',
    todaysRule: "Eva's own judgement. Safety issues are always high, whatever is written here.",
  },
  {
    key: 'medium',
    label: 'What medium priority means for this company',
    todaysRule: 'A complaint is medium at least.',
  },
  {
    key: 'low',
    label: 'What low priority means for this company',
    todaysRule: 'Only praise, suggestions and simple questions with no complaint.',
  },
];

export const AUTO_SEND_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
] as const;
export const DEFAULT_AUTO_SEND_PRIORITIES: string[] = ['low'];

export const AUTO_SEND_REPLY_TYPE_OPTIONS = [
  { value: 'acknowledgement', label: 'Acknowledgement' },
  { value: 'thank_you_feedback', label: 'Thank you for feedback' },
  { value: 'general_query_answer', label: 'General question answer' },
  { value: 'request_missing_info', label: 'Ask for more information' },
] as const;
export const DEFAULT_AUTO_SEND_REPLY_TYPES: string[] = [
  'acknowledgement',
  'thank_you_feedback',
  'general_query_answer',
];

/** Every category except food safety, allergen and legal or media, which always go to a person. */
export const AUTO_SEND_CATEGORY_OPTIONS = [
  { value: 'staff_conduct', label: 'Staff conduct' },
  { value: 'order_accuracy', label: 'Order accuracy' },
  { value: 'food_quality', label: 'Food quality' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'service_speed', label: 'Service speed' },
  { value: 'cleanliness', label: 'Cleanliness' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'digital', label: 'Digital' },
  { value: 'refund_payment', label: 'Refund or payment' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'general_query', label: 'General question' },
  { value: 'other', label: 'Other' },
] as const;
export const DEFAULT_AUTO_SEND_CATEGORIES: string[] = AUTO_SEND_CATEGORY_OPTIONS.map(
  (o) => o.value,
);
